import { UploadFile, CheckCircle, DeleteOutline, InsertDriveFile } from '@mui/icons-material';
import { useRef } from 'react';

interface DocumentUploadCardProps {
  label: string;
  documentType: string;
  uploadedFile?: { id: string; originalFilename: string; fileSize: number };
  onUpload: (documentType: string, file: File) => void;
  onDelete?: (documentId: string) => void;
  /** The documentType currently being uploaded — only that card shows the loading state */
  uploadingDocType?: string | null;
  accept?: string;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const DocumentUploadCard: React.FC<DocumentUploadCardProps> = ({
  label,
  documentType,
  uploadedFile,
  onUpload,
  onDelete,
  uploadingDocType = null,
  accept = '.pdf,.jpg,.jpeg,.png',
}) => {
  const isUploading = uploadingDocType === documentType;
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) onUpload(documentType, file);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUpload(documentType, file);
    if (inputRef.current) inputRef.current.value = '';
  };

  if (uploadedFile) {
    return (
      <div className="border border-green-200 bg-green-50 rounded-lg p-3 flex items-center gap-3">
        <CheckCircle className="text-green-500 flex-shrink-0" fontSize="small" />
        <InsertDriveFile className="text-gray-400 flex-shrink-0" fontSize="small" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">
            {uploadedFile.originalFilename}
          </p>
          <p className="text-xs text-gray-500">{formatBytes(uploadedFile.fileSize)}</p>
        </div>
        {onDelete && (
          <button
            onClick={() => onDelete(uploadedFile.id)}
            className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
            title="Eliminar documento"
          >
            <DeleteOutline fontSize="small" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
        isUploading
          ? 'border-blue-300 bg-blue-50'
          : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
      }`}
      role="button"
      tabIndex={0}
      onDragOver={e => e.preventDefault()}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleChange}
      />
      <UploadFile className="text-gray-400 mb-1" fontSize="medium" />
      <p className="text-sm font-medium text-gray-700">{label}</p>
      <p className="text-xs text-gray-400 mt-0.5">
        {isUploading ? 'Subiendo...' : 'Arrastra o haz clic para subir'}
      </p>
    </div>
  );
};
